<template>
  <div class="engagement-step" :class="['engagement-step--' + stepStatus, {'engagement-step--excluded': !step.included}]" :data-qid="'engagement-step-' + step.step">
    <div class="engagement-step__icon">
      <font-awesome-icon :icon="statusIcon" />
    </div>

    <div class="engagement-step__body">
      <div class="engagement-step__header">
        <div class="engagement-step__title">
          {{ $t('projects.engagement.step.' + step.step) }}
        </div>

        <div class="engagement-step__status" v-if="step.included">
          <b-dropdown v-if="editable && !step.derived"
                      size="sm"
                      variant="link"
                      right
                      no-caret
                      toggle-class="engagement-status-pill"
                      :class="'engagement-status engagement-status--' + step.status"
                      :data-qid="'engagement-step-status-' + step.step"
          >
            <template #button-content>
              {{ $t('projects.engagement.status.' + step.status) }}
              <font-awesome-icon icon="chevron-down" class="ml-1"/>
            </template>
            <b-dropdown-item v-for="status in statuses"
                             :key="status"
                             :active="step.status === status"
                             @click="changeStatus(status)"
            >
              {{ $t('projects.engagement.status.' + status) }}
            </b-dropdown-item>
          </b-dropdown>
          <span v-else class="engagement-status-pill" :class="'engagement-status--' + step.status">
            {{ $t('projects.engagement.status.' + step.status) }}
          </span>
        </div>
        <div class="engagement-step__status" v-else>
          <span class="engagement-status-pill engagement-status--not_applicable">
            {{ $t('projects.engagement.status.not_applicable') }}
          </span>
        </div>
      </div>

      <div class="engagement-step__description" v-if="showDescription">
        {{ $t('projects.engagement.step_description.' + step.step) }}
      </div>

      <template v-if="step.included">
        <!-- Client evaluation builds on the independence declarations of the team -->
        <div class="engagement-step__extra" v-if="step.step === 'client_evaluation' && project.include_independence_declaration">
          <font-awesome-icon icon="user-check" class="mr-1"/>
          {{ $t('projects.engagement.step_ui.independence_summary', { approved: independenceCounts.approved, total: independenceCounts.total }) }}
        </div>

        <div class="engagement-step__extra" v-if="step.step === 'business_understanding' && project.include_mapping">
          <a href="#" @click.prevent="$emit('open-tab', 'system_description')" data-qid="engagement-open-system-description">
            <font-awesome-icon icon="external-link-alt" class="mr-1"/>
            {{ $t('projects.engagement.step_ui.open_system_description') }}
          </a>
        </div>

        <!-- Execution steps follow the tasks -->
        <div class="engagement-step__progress" v-if="step.derived && step.progress" :data-qid="'engagement-step-progress-' + step.step">
          <div class="engagement-budget__bar">
            <span :style="{ width: progressPercent + '%' }" :class="{'engagement-budget__bar--done': step.status === 'completed'}"></span>
          </div>
          <span class="engagement-step__progress-text">
            {{ $t('projects.engagement.step_ui.' + progressLabel, { done: step.progress.done, total: step.progress.total }) }}
          </span>
        </div>

        <!-- Evidence review: quality review of the tasks -->
        <div class="engagement-step__review" v-if="step.step === 'evidence_review' && review" data-qid="engagement-review-tallies">
          <span class="engagement-review-pill engagement-review-pill--accepted"
                v-b-tooltip.hover
                :title="$t('projects.engagement.review.hint', { count: review.required_sign_offs })"
          >
            <font-awesome-icon icon="check-circle" class="mr-1"/>{{ $t('projects.engagement.review.accepted_count', { count: review.accepted, total: review.total }) }}
          </span>
          <span class="engagement-review-pill engagement-review-pill--waiting">
            <font-awesome-icon icon="exclamation-triangle" class="mr-1"/>{{ $t('projects.engagement.review.waiting_count', { count: review.waiting }) }}
          </span>
          <span class="engagement-review-pill engagement-review-pill--rejected">
            <font-awesome-icon icon="times-circle" class="mr-1"/>{{ $t('projects.engagement.review.rejected_count', { count: review.rejected }) }}
          </span>
        </div>

        <!-- Evidence collection: a tally of the evidence library; files and task links are on the Documents tab -->
        <div v-if="step.step === 'evidence_collection' && evidence" class="engagement-step__evidence" data-qid="engagement-evidence-tally">
          <span class="engagement-review-pill engagement-review-pill--neutral">
            <font-awesome-icon icon="paperclip" class="mr-1"/>{{ $t('projects.engagement.evidence.files_count', { count: evidence.files }) }}
          </span>
          <span class="engagement-review-pill"
                :class="evidence.unlinked ? 'engagement-review-pill--waiting' : 'engagement-review-pill--accepted'"
          >
            <font-awesome-icon icon="link" class="mr-1"/>{{ $t('projects.engagement.evidence.unlinked_count', { count: evidence.unlinked }) }}
          </span>
          <a href="#"
             class="ml-1"
             @click.prevent="$emit('open-tab', 'documents')"
             data-qid="engagement-evidence-open-documents"
          >
            {{ $t('projects.engagement.evidence.manage_on_documents') }}
            <font-awesome-icon icon="external-link-alt" class="ml-1"/>
          </a>
          <file-upload v-if="editable"
                       ref="evidence-uploader"
                       v-model="files"
                       input-id="engagement-upload-evidence_collection"
                       :multiple="true"
                       @input="upload"
                       class="engagement-file__upload text-success cursor-pointer ml-2"
                       data-qid="engagement-step-upload-evidence_collection"
          >
            <font-awesome-icon icon="cloud-upload-alt" class="mr-1"/>
            {{ $t('projects.engagement.step_ui.upload') }}
          </file-upload>
        </div>

        <div class="engagement-step__documents" v-if="hasDocuments && step.step !== 'evidence_collection'">
          <span v-for="file in documents"
                :key="'step-file-' + file.id"
                class="engagement-file"
          >
            <font-awesome-icon icon="paperclip" class="mr-1"/>
            <a href="#" @click.prevent="openDocument(file)" data-qid="engagement-step-file">{{ file.file_name }}</a>
            <span v-if="editable"
                  class="engagement-file__remove cursor-pointer"
                  v-b-tooltip.hover
                  :title="$t('attachments.delete.title')"
                  @click="openDeleteModal(file)"
                  data-qid="engagement-step-file-remove"
            >
              <font-awesome-icon icon="times"/>
            </span>
          </span>
          <span v-if="!documents.length" class="text-muted engagement-step__no-documents">
            {{ $t('projects.engagement.step_ui.no_documents') }}
          </span>

          <file-upload
              v-if="editable"
              :ref="'uploader-' + step.step"
              v-model="files"
              :input-id="'engagement-upload-' + step.step"
              :multiple="true"
              @input="upload"
              class="engagement-file__upload text-success cursor-pointer"
              :data-qid="'engagement-step-upload-' + step.step"
          >
            <font-awesome-icon icon="cloud-upload-alt" class="mr-1"/>
            {{ $t('projects.engagement.step_ui.upload') }}
          </file-upload>
        </div>

        <div class="engagement-step__meta" v-if="step.updated_by && step.status !== 'not_started'">
          {{ $t('projects.engagement.step_ui.updated_by', { name: step.updated_by.name }) }}
          <span class="ml-1">{{ step.updated_at | formatDate }}</span>
        </div>
      </template>
    </div>

    <ConfirmRemoveAttachment
        :object="selectedFile"
        :show="showDeleteModal"
        type="file"
        @closeConfirmModal="closeDeleteModal"
    ></ConfirmRemoveAttachment>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import { openDocument as openDocumentInTab } from '@/util/openDocument.js';
import { STEP_STATUSES, STEPS_WITHOUT_DOCUMENTS } from '@/views/Projects/Engagement/engagement.js';

export default {
  name: 'EngagementStep',
  props: {
    project: {
      type: Object,
      required: true
    },
    step: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    showDescription: {
      type: Boolean,
      default: true
    },
    // Quality review tallies of the tasks, shown on the Evidence review step
    review: {
      type: Object,
      default: null
    },
    // Evidence library counts, shown on the Evidence collection step
    evidence: {
      type: Object,
      default: null
    }
  },
  components: {
    ConfirmRemoveAttachment
  },
  data: function() {
    return {
      statuses: STEP_STATUSES,
      files: [],
      selectedFile: {},
      showDeleteModal: false
    }
  },
  computed: {
    stepStatus() {
      return this.step.included ? this.step.status : 'not_applicable';
    },
    statusIcon() {
      const icons = {
        completed: 'check-circle',
        in_progress: 'adjust',
        not_started: 'circle',
        not_applicable: 'minus-circle'
      };
      return icons[this.stepStatus];
    },
    hasDocuments() {
      return !STEPS_WITHOUT_DOCUMENTS.includes(this.step.step);
    },
    progressPercent() {
      const progress = this.step.progress || {};
      return progress.total ? Math.round(progress.done / progress.total * 100) : 0;
    },
    progressLabel() {
      return this.step.step === 'evidence_collection' ? 'evidence_progress' : 'task_progress';
    },
    documents() {
      return _.filter(this.project.media || [], file => file.collection_name === this.step.collection);
    },
    independenceCounts() {
      const statuses = this.project.users_independence || {};
      const users = this.project.users || [];
      return {
        approved: _.filter(users, user => statuses[user.id] === 1).length,
        total: users.length
      };
    }
  },
  methods: {
    changeStatus(status) {
      let self = this;
      if (status === this.step.status) {
        return;
      }

      piincHttp.put('projects/' + this.project.slug + '/engagement/steps/' + this.step.step, {
        status: status
      }).then(function(response) {
        self.$toastr('success', self.$t('projects.engagement.step_ui.status_success'));
        self.$emit('changed', response.data);
      }, function(error) {
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    upload() {
      let self = this;
      if (!this.files.length) {
        return;
      }

      let formData = new FormData();
      for (let i = 0; i < this.files.length; i++) {
        if (this.files[i].size > 10485760) {
          this.$toastr('error', this.$t('system.file_size'));
          this.files = [];
          return;
        }
        formData.append('document[]', this.files[i].file);
      }

      formData.append('name', 'documents');
      formData.append('collection', this.step.collection);
      formData.append('model_type', this.project.who_am_i);
      formData.append('model_id', this.project.id);

      piincHttp.post('documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(function() {
        self.files = [];
        self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'));
        self.$emit('media-changed');
      }, function(error) {
        self.files = [];
        let message = error.data && error.data.message ? error.data.message : '';
        if (error.data && error.data.errors && error.data.errors['document.0']) {
          message += ' ' + error.data.errors['document.0'][0];
        }
        self.$toastr('error', message);
      });
    },
    openDocument(file) {
      // Images, PDFs and Office files open in a new browser tab
      openDocumentInTab(file);
    },
    downloadMedia(file) {
      piincHttp.get('documents/' + file.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = file.file_name;

        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    openDeleteModal(file) {
      this.selectedFile = file;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.selectedFile = {};
      this.showDeleteModal = false;
      this.$emit('media-changed');
    }
  }
}
</script>
